import { Component, Input, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core'
import { Store, select } from '@ngrx/store'
import { selectConnectionsByPropertyId } from '@tv3/store/connection/connection.selectors'
import {
  Channel,
  ChannelService,
  compactObject,
  ConnectedChannelAccountResponse,
  Destroyable,
  isSomething,
  untilDestroy,
} from '@tokeet-frontend/tv3-platform'
import { Connection } from '@tv3/store/connection/connection.model'
import { MatPaginator } from '@angular/material/paginator'
import { MatSort } from '@angular/material/sort'
import { MatTableDataSource } from '@angular/material/table'
import { debounceTime, filter, map, startWith, switchMap, tap } from 'rxjs/operators'
import { AirbnbChannelService, type AirbnbReviewSubmittedResponse } from '@tokeet-frontend/channels'
import { combineLatest, of } from 'rxjs'
import * as lodash from 'lodash'
import { isEmptyTable, localeCompareSort } from '@tokeet-frontend/tv3-platform'
import { AbbReplyGuestReviewDialogService } from '../reply-guest-review-dialog/abb-reply-guest-review-dialog.service'
import moment from 'moment'
import { UntypedFormBuilder } from '@angular/forms'
import { TableType } from '@tv3/shared/empty-table/table-type'
import { ChannelReviewSearchParams } from '@tokeet-frontend/reviews'

interface AirbnbReviewTableItem extends AirbnbReviewSubmittedResponse {
  roomName: string
  review: string
  rating: number
  roomId: any
  propertyId: any
  submittedDate: number
}

@Component({
  selector: 'app-abb-reviews',
  templateUrl: './abb-reviews.component.html',
  styleUrls: ['./abb-reviews.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AbbReviewsComponent extends Destroyable implements OnInit {
  @Input() channel: Channel

  @ViewChild('paginator', { static: true }) paginator: MatPaginator
  @ViewChild(MatSort, { static: true }) sort: MatSort

  dataSource = new MatTableDataSource<AirbnbReviewTableItem>()
  displayedColumns = ['submittedDate', 'roomName', 'propertyId', 'roomId', 'review', 'rating', 'edit']
  isEmptyTable$ = isEmptyTable(this.dataSource)
  tableType = TableType.AirbnbReviews

  filtersForm = this.fb.group({
    property: [],
    room: [],
    period: [null],
    rating: [null],
  })

  connectedAccounts: ConnectedChannelAccountResponse[] = []
  rooms: {
    name: string
    id: string
    hotelName: string
  }[] = []

  constructor(
    private store: Store<any>,
    private fb: UntypedFormBuilder,
    private channelService: ChannelService,
    private airbnbChannelService: AirbnbChannelService,
    private abbReplyGuestReviewDialog: AbbReplyGuestReviewDialogService
  ) {
    super()
  }

  ngOnInit(): void {
    this.dataSource.paginator = this.paginator
    this.dataSource.sort = this.sort
    this.dataSource.sortData = localeCompareSort

    combineLatest([
      this.filtersForm.get('property').valueChanges.pipe(
        tap((propertyId) => {
          this.rooms = lodash.find(this.connectedAccounts, (t) => t.propertyId === propertyId)?.rooms
          this.filtersForm.get('room').reset()
        })
      ),
      this.filtersForm.get('room').valueChanges.pipe(startWith('')),
    ])
      .pipe(
        switchMap(([propertyId, listingId]) =>
          this.store.pipe(
            select(selectConnectionsByPropertyId(propertyId)),
            debounceTime(500),
            switchMap((items: Connection[]) => {
              items = listingId ? lodash.filter(items, (t) => t.roomId == listingId) : items
              if (!isSomething(items)) return of([])
              return combineLatest(
                items.map((item) => {
                  return this.airbnbChannelService
                    .getListingReviews(item.roomId)
                    .pipe(
                      map((reviews) =>
                        lodash.map(reviews, (review: AirbnbReviewSubmittedResponse) =>
                          this.toReviewTableItem(review, item)
                        )
                      )
                    )
                })
              )
            }),
            map((items) => lodash.flatten(items))
          )
        ),
        switchMap((items) =>
          combineLatest([
            this.filtersForm.get('period').valueChanges.pipe(startWith(this.filtersForm.get('period').value)),
            this.filtersForm.get('rating').valueChanges.pipe(startWith(this.filtersForm.get('rating').value)),
          ]).pipe(
            map(([period, rating]) => {
              let filteredItems = items
              if (period) {
                filteredItems = filteredItems.filter(
                  (t) => t.submittedDate >= period.from && t.submittedDate <= period.to
                )
              }
              if (rating !== null) {
                filteredItems = filteredItems.filter((t) => Math.floor(t.rating) === rating)
              }
              return filteredItems
            })
          )
        ),
        untilDestroy(this)
      )
      .subscribe((items) => {
        this.dataSource.data = items
      })

    this.channelService.getConnectedAccounts(this.channel.id).subscribe((accounts) => {
      this.connectedAccounts = accounts
      this.filtersForm.get('property').setValue(lodash.head(accounts)?.propertyId)
      this.rooms = lodash.head(accounts)?.rooms
    })
  }

  toReviewTableItem(review: AirbnbReviewSubmittedResponse, listing: Connection): AirbnbReviewTableItem {
    return {
      ...review,
      submittedDate: review.submitted_at ? moment(review.submitted_at).unix() : 0,
      roomName: listing.roomName,
      propertyId: listing.propertyId,
      roomId: listing.roomId,
      review: review.public_review,
      rating: review.overall_rating,
    }
  }

  onEdit(review: AirbnbReviewTableItem) {
    this.abbReplyGuestReviewDialog.open(review, review.propertyId, review.roomId)
  }

  onSearch(term: string) {
    this.paginator.firstPage()
    this.dataSource.filter = (term + '').trim().toLowerCase()
  }
}
